/**
 * registrationToken.ts
 *
 * The Cognito OAuth `id_token` is only available in `window.location.hash`
 * for a very short window right after the `/loggedin` redirect. Once React
 * Router navigates to another route (e.g. company registration), the hash
 * can no longer be relied upon.
 *
 * This utility captures the token once, right after login, and stores it in
 * `sessionStorage` so pages further down the registration flow (which may be
 * reached via client-side navigation) can still read it. sessionStorage is
 * cleared automatically when the tab closes and is cleared explicitly once
 * the registration flow finishes or is aborted.
 */

const STORAGE_KEY = 'registrationIdToken';

/**
 * Reads the `id_token` directly from the current URL hash.
 * Only valid immediately after the Cognito OAuth redirect.
 */
function readTokenFromHash(): string
{
    const hash = window.location.hash;
    const hasToken = hash.indexOf('id_token=') > -1;
    if (!hasToken) return '';
    return hash.split('id_token=')[1].split('&')[0];
}

/**
 * Captures the `id_token` from the URL hash (if present) and persists it in
 * sessionStorage. Call this exactly once, as early as possible after the
 * OAuth redirect (e.g. in LoggedInPage), before any navigation happens.
 */
export function captureRegistrationToken(): void
{
    const token = readTokenFromHash();
    if (token)
    {
        sessionStorage.setItem(STORAGE_KEY, token);
    }
}

/**
 * Returns the token captured for the registration flow. Falls back to
 * reading the hash directly for backwards compatibility (e.g. if a
 * component is opened as an in-page dialog while the hash is still fresh).
 */
export function getRegistrationToken(): string
{
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    return readTokenFromHash();
}

/**
 * Clears the stored registration token. Call this once the registration
 * flow completes (company created) or is aborted, so the token does not
 * linger in sessionStorage longer than necessary.
 */
export function clearRegistrationToken(): void
{
    sessionStorage.removeItem(STORAGE_KEY);
}
